import { XMarkIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import type { RespectDTO } from '../../services/api/respect';

interface RespectBreakdownPanelProps {
  data: RespectDTO;
  onClose: () => void;
}

function relativeTime(isoString: string): string {
  try {
    return formatDistanceToNow(new Date(isoString), { addSuffix: true });
  } catch {
    return isoString;
  }
}

export function RespectBreakdownPanel({ data, onClose }: RespectBreakdownPanelProps) {
  const categoryEntries = Object.entries(data.perCategoryBreakdown);
  const behaviourEntries = Object.entries(data.behaviourBreakdown);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="breakdown-panel-title"
    >
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <h2 id="breakdown-panel-title" className="text-lg font-semibold text-gray-900">
            Respect Breakdown
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close breakdown panel"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6" tabIndex={0}>
          {/* Per-category breakdown */}
          {categoryEntries.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Recognition by category
              </h3>
              <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                {categoryEntries.map(([label, points]) => (
                  <div key={label} className="flex items-center justify-between px-4 py-2">
                    <dt className="text-sm text-gray-700">{label}</dt>
                    <dd className="text-sm font-medium text-gray-900">{points} pts</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {/* Behaviour breakdown */}
          {behaviourEntries.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Behaviour signals
              </h3>
              <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                {behaviourEntries.map(([source, points]) => (
                  <div key={source} className="flex items-center justify-between px-4 py-2">
                    <dt className="text-sm text-gray-700">{source}</dt>
                    <dd className="text-sm font-medium text-gray-900">{points} pts</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {/* Recent kudos feed */}
          {data.recentKudos.length > 0 ? (
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Recent kudos
              </h3>
              <ul className="space-y-3">
                {data.recentKudos.map((kudos) => (
                  <li
                    key={kudos.id}
                    className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-800">
                        {kudos.giver.name}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {kudos.tag.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{kudos.message}</p>
                    <p className="mt-1 text-xs text-gray-400">{relativeTime(kudos.createdAt)}</p>
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No kudos received yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
